<!-- eslint-disable vue/multi-word-component-names -->
<script>
/* eslint-disable no-undef */
import {
  computed,
  onMounted,
  onUnmounted,
  watch,
  reactive,
  ref,
  defineEmits,
} from 'vue';
import { Loader } from '@googlemaps/js-api-loader';
import markerRed from '../../stories/assets/marker-red.svg';
import markerBlack from '../../stories/assets/marker-black.svg';
import cluster from '../../stories/assets/cluster.svg';
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';
const mapStyles = [
  {
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'administrative.land_parcel',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'administrative.neighborhood',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'transit',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
];

export default {
  props: {
    apiKey: {
      type: String,
      required: true,
    },
    selectedDeparture: {
      type: String,
      required: true,
    },
    hoverDestination: {
      type: String,
      default: '',
    },
    selectedDestination: {
      type: String,
      default: '',
    },
    isHovering: {
      type: Boolean,
      default: false,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    allAirports: {
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: () => [],
    },
    locations: {
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: () => [],
    },
  },

  setup(props, { emit }) {
    const loader = new Loader({
      apiKey: props.apiKey,
    });
    let newCluster;
    let map;
    let departureMarker;
    const markers = [];
    const infoWindows = [];
    const selectedInfoWindows = [];
    const selectedMarkers = [];
    const hoverPolylinePath = reactive([]);
    const selectedPolylinePath = reactive([]);
    const isHovering = ref(false);
    const isLoaded = ref(false);
    const departLocationName = ref('');
    const destinationLocationName = ref('');
    const selectedDestinationLocationName = ref('anywhere');
    const mouseOverInfoWindow = ref(false);
    const currencyCode = ref('KES');
    const polylineInstance = computed(() => {
      const lineSymbol = {
        path: 'M0 0.5C0 0.223858 0.223858 0 0.5 0H6.5C6.77614 0 7 0.223858 7 0.5C7 0.776142 6.77614 1 6.5 1H0.5C0.223858 1 0 0.776142 0 0.5Z',
        strokeOpacity: 1,
        scale: 0.8,
        rotation: 90,
        fill: '#0D0D0D',
      };
      if (hoverPolylinePath.length > 1) {
        return new google.maps.Polyline({
          map: map,
          geodesic: true,
          strokeOpacity: 0,
          path: hoverPolylinePath,
          icons: [
            {
              icon: lineSymbol,
              repeat: '11px',
            },
          ],
        });
      } else {
        return null;
      }
    });

    const selectedPolylineInstance = computed(() => {
      if (selectedMarkers.length >= 2) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        selectedPolylinePath.length = 0;
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        selectedPolylinePath.unshift(selectedMarkers[0].getPosition());
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        selectedPolylinePath.push(
          selectedMarkers[selectedMarkers.length - 1].getPosition(),
        );
        selectedPolylineInstance.value?.setMap(null);
        return new google.maps.Polyline({
          map: map,
          strokeColor: '#000000',
          strokeOpacity: 1,
          strokeWeight: 3,
          path: selectedPolylinePath,
          geodesic: true,
        });
      } else {
        return null;
      }
    });

    watch(
      () => props.hoverDestination,
      () => {
        destinationLocationName.value = props.hoverDestination;
      },
    );

    watch([() => props.selectedDeparture, () => props.locations], () => {
      removeAllMarkers(departureMarker, markers);
      if (isLoaded.value) {
        selectedMarkers.length = 0;
        infoWindows.forEach((e) => {
          e.close();
          polylineInstance.value?.setMap(null);
        });
        if (selectedPolylinePath.length > 0) {
          selectedPolylineInstance.value.setMap(null);
        }
        markers.length = 0;
        createMarkers(
          map,
          polylineInstance,
          selectedPolylineInstance,
          props.locations,
        );
        createDepartureMarker(map, props.selectedDeparture);
        createClusters(map);
        selectedMarkers.length = 1;
      }
    });
    watch(
      () => props.selectedDestination,
      () => {
        if (
          props.selectedDestination === null &&
          selectedMarkers[1] !== undefined
        ) {
          selectedPolylineInstance.value.setMap(null);
          const index = markers.findIndex((marker) => {
            return (
              marker.name === selectedMarkers[1].name.trim().toLocaleLowerCase()
            );
          });
          if (index !== -1) {
            selectedDestinationLocationName.value = '';
            handleClick(
              markers[index],
              selectedPolylineInstance,
              infoWindows[index],
            );
            selectedInfoWindows.forEach((e) => {
              e.close();
            });
          }
        } else if (props.selectedDestination !== null) {
          selectedDestinationLocationName.value =
            props.selectedDestination.locCity.trim().toLocaleLowerCase();
        }
      },
    );

    watch(
      () => props.isHovering,
      () => {
        isHovering.value = props.isHovering;
      },
    );

    watch(
      [
        () => departLocationName.value,
        () => destinationLocationName.value,
        () => isHovering.value,
      ],
      () => {
        if (isHovering.value === true) {
          let index = markers.findIndex((marker) => {
            return (
              marker.name ===
              destinationLocationName.value.trim().toLocaleLowerCase()
            );
          });
          if (index !== -1) {
            handleMouseOver(markers[index], infoWindows[index]);
          }
          return;
        }
        if (isHovering.value === false) {
          polylineInstance.value?.setMap(null);
          const index = markers.findIndex((marker) => {
            return (
              marker.name ===
              destinationLocationName.value.trim().toLocaleLowerCase()
            );
          });
          if (index !== -1) {
            handleMouseOut(infoWindows[index], markers[index]);
          }
        }
      },
    );
    watch([() => selectedDestinationLocationName.value], () => {
      if (selectedDestinationLocationName.value !== '') {
        if (selectedMarkers.length === 2) {
          selectedInfoWindows.forEach((e) => {
            e.close();
          });
        }
        let index = markers.findIndex((marker) => {
          return (
            marker.name ===
            selectedDestinationLocationName.value.trim().toLocaleLowerCase()
          );
        });
        if (index !== -1) {
          handleClick(
            markers[index],
            selectedPolylineInstance,
            infoWindows[index],
          );
        }
        return;
      }
    });

    onMounted(async () => {
      await loader
        .load()
        .then((google) => {
          map = new google.maps.Map(document.getElementById('map'), {
            center: { lat: 20.020371, lng: 7.686484 },
            disableDefaultUI: true,
            zoomControl: true,
            keyboardShortcuts: false,
            styles: mapStyles,
            tilt: 0,
          });
          createMarkers(
            map,
            polylineInstance,
            selectedPolylineInstance,
            props.locations,
          );
          createDepartureMarker(map, props.selectedDeparture);
          createClusters(map);
        })
        .catch((error) => {
          console.log('error');
          console.log(error);
        });
    });
    const createClusters = (map) => {
      const icon = {
        url: cluster,
        scaledSize: new google.maps.Size(35, 35),
      };
      const renderer = {
        render: ({ count, position }) =>
          new google.maps.Marker({
            label: {
              text: String(count),
              color: 'white',
              fontSize: '14px',
            },
            position,
            zIndex: 2,
            icon: icon,
          }),
      };
      const onClusterClick = (e, cluster, marker) => {
        const clusterClickBounds = new google.maps.LatLngBounds();
        cluster.markers.forEach((e) => {
          clusterClickBounds.extend(e.getPosition());
        });
        clusterClickBounds.extend(cluster._position);
        map.fitBounds(clusterClickBounds);
        map.setZoom(5);
      };
      const algorithm = new SuperClusterAlgorithm({ maxZoom: 2 });
      newCluster = new MarkerClusterer({
        map,
        markers,
        renderer,
        algorithm,
        onClusterClick,
      });
    };

    const removeAllMarkers = (departureMarker, markers) => {
      if (departureMarker !== undefined) {
        departureMarker.setMap(null);
        departureMarker = undefined;
      }
      if (markers.length > 0) {
        newCluster.clearMarkers();
        markers.forEach((e) => {
          e.setMap(null);
        });

        markers.length = 0;
        infoWindows.length = 0;
      }
    };
    const createDepartureMarker = (map, airport) => {
      departureMarker = new google.maps.Marker({
        position: airport.coords,
        map: map,
        zIndex: 10,
        icon: {
          url: markerBlack,
          anchor: new google.maps.Point(8, 8),
          scaledSize: new google.maps.Size(12, 12),
        },
        name: airport.locCity.trim().toLocaleLowerCase(),
      });
      selectedMarkers.push(departureMarker);
    };
    const removeFromCluster = (marker) => {
      newCluster.removeMarker(marker);
      marker.setMap(map);
    };
    const createMarkers = (
      map,
      polylineInstance,
      selectedPolylineInstance,
      locations,
    ) => {
      isLoaded.value = true;
      let modifiedPrice = 0;
      locations.forEach((airport, index) => {
        if (airport.currencyCode !== '') {
          currencyCode.value = airport.currencyCode;
        }
        if (airport.currencyDecimalPlaces !== 0) {
          let priceString = airport.minPrice.toString();
          let modifiedPriceString =
            priceString.slice(0, -airport.currencyDecimalPlaces) +
            '.' +
            priceString.slice(-airport.currencyDecimalPlaces);
          modifiedPrice = parseFloat(modifiedPriceString).toFixed(
            airport.currencyDecimalPlaces,
          );
        }
        const marker = new google.maps.Marker({
          position: airport.coords,
          map: map,
          zIndex: 999999999999999,
          icon: {
            url: markerRed,
            anchor: new google.maps.Point(8, 8),
            scaledSize: new google.maps.Size(12, 12),
          },
          name: airport.locCity.trim().toLocaleLowerCase(),
        });
        marker.metadata = { name: airport.locCity.trim().toLocaleLowerCase() };
        if (
          props.selectedDeparture ===
            airport.locCity.trim().toLocaleLowerCase() &&
          selectedMarkers.length > 0
        ) {
          selectedMarkers.unshift(departureMarker);
        }
        let infoWindow;
        infoWindow = new google.maps.InfoWindow({
          content:
            `<div id="${airport.locCity
              .trim()
              .toLocaleLowerCase()}"  class="infowindow">` +
            `<div> <p class="heading-h6 text-KQ-Grey1">${airport.locCity}</p>` +
            `<p class="paragraph-note text--bold text-KQ-Grey1">From ${
              currencyCode.value
            } ${
              modifiedPrice !== 0 ? modifiedPrice : airport.minPrice
            }</p></div>
              <div class=" items-center infowindow__icon"><i class="icon icon-chevronright icon-color--red icon--xxs" aria-hidden="true" />
              </div</div>`,
        });
        infoWindows.push(infoWindow);
        marker.addListener('mouseover', () => {
          polylineInstance.value?.setMap(null);
          destinationLocationName.value = airport.locCity;
          isHovering.value = true;
        });
        marker.addListener(map, 'zoom_changed', () => {
          polylineInstance.value?.setMap(null);
          infoWindows.forEach((e) => {
            e.close();
          });
        });
        marker.addListener('mouseout', (e) => {
          isHovering.value = false;
        });
        marker.addListener('click', () => {
          if (selectedMarkers.length === 2) {
            selectedInfoWindows.forEach((e) => {
              e.close();
              polylineInstance.value?.setMap(null);
            });
          }
          if (
            selectedMarkers.length === 2 &&
            selectedMarkers[selectedMarkers.length - 1].name === marker.name &&
            selectedDestinationLocationName.value === marker.name
          ) {
            emit('selectedDestinationName', null);
            selectedPolylineInstance.value?.setMap(null);
            selectedMarkers[selectedMarkers.length - 1].setIcon({
              url: markerRed,
              anchor: new google.maps.Point(8, 8),
              scaledSize: new google.maps.Size(12, 12),
            });
            selectedMarkers.splice(1, 1);
            selectedPolylineInstance.value?.setMap(null);
            closeInfoWindow(infoWindow);
          } else {
            handleClick(marker, selectedPolylineInstance, infoWindow);
          }
        });
        markers.push(marker);
        return marker;
      });
      map.setOptions({ minZoom: 3, maxZoom: 15 });
      map.setZoom(3);
    };
    const handleClick = (marker, selectedPolylineInstance, infoWindow) => {
      infoWindow.open(map, marker);
      selectedInfoWindows.push(infoWindow);

      if (
        selectedMarkers.some((e) => e.name === marker.name) &&
        selectedDestinationLocationName.value === ''
      ) {
        selectedInfoWindows.forEach((e) => {
          e.close();
        });
        marker.setIcon({
          url: markerRed,
          anchor: new google.maps.Point(8, 8),
          scaledSize: new google.maps.Size(12, 12),
        });
        selectedMarkers.splice(selectedMarkers.indexOf(marker), 1);
        selectedPolylineInstance.value?.setMap(null);
        emit('selectedDestinationName', null);
      } else {
        if (
          selectedMarkers.length === 2 &&
          selectedMarkers[selectedMarkers.length - 1].name !== marker.name
        ) {
          removeFromCluster(marker);
          emit(
            'selectedDestinationName',
            marker.name.trim().toLocaleLowerCase(),
          );
          selectedMarkers[selectedMarkers.length - 1].setIcon({
            url: markerRed,
            anchor: new google.maps.Point(8, 8),
            scaledSize: new google.maps.Size(12, 12),
          });
          selectedMarkers.splice(1, 1);
          selectedMarkers.push(marker);
          selectedMarkers[1].setIcon({
            url: markerBlack,
            anchor: new google.maps.Point(8, 8),
            scaledSize: new google.maps.Size(12, 12),
          });
          selectedMarkers.forEach((item) => {
            selectedPolylinePath.push(item.getPosition());
          });
          setTimeout(function () {
            return selectedPolylineInstance.value;
          }, 50);
          if (infoWindow) {
            google.maps.event.addListener(infoWindow, 'domready', function () {
              addOpenInfoWindowListeners(infoWindow, marker);
            });
          }
        } else if (selectedMarkers.length < 2) {
          emit(
            'selectedDestinationName',
            marker.name.trim().toLocaleLowerCase(),
          );
          selectedMarkers.push(marker);
          selectedMarkers.forEach((item) => {
            selectedPolylinePath.push(item.getPosition());
          });
          removeFromCluster(marker);
          selectedMarkers[1].setIcon({
            url: markerBlack,
            anchor: new google.maps.Point(8, 8),
            scaledSize: new google.maps.Size(12, 12),
          });
          setTimeout(function () {
            return selectedPolylineInstance.value;
          }, 50);
          selectedDestinationLocationName.value = '';
          if (infoWindow) {
            google.maps.event.addListener(infoWindow, 'domready', function () {
              addOpenInfoWindowListeners(infoWindow, marker);
            });
          }
        }
      }
    };
    onUnmounted(() => {
      map.value = null;
    });
    const handleMouseOver = (marker, infoWindow) => {
      polylineInstance.value?.setMap(null);
      if (selectedMarkers[0].name !== marker.name) {
        infoWindow?.open(map, marker);
        const oldestSelectedMarker = selectedMarkers[0];
        hoverPolylinePath.splice(0, hoverPolylinePath.length);
        hoverPolylinePath.push(
          marker.getPosition(),
          oldestSelectedMarker.getPosition(),
        );
        if (infoWindow) {
          google.maps.event.addListener(infoWindow, 'domready', function () {
            addOpenInfoWindowListeners(infoWindow, marker);
          });
        }
        return polylineInstance.value;
      }
    };
    const handleMouseOut = (infoWindow, marker) => {
      setTimeout(function () {
        let selectedMarker = selectedMarkers.some(
          (e) => e.name === marker.name,
        );
        if (!mouseOverInfoWindow.value && selectedMarkers.length < 2) {
          closeInfoWindow(infoWindow);
        } else if (
          selectedMarkers.length >= 2 &&
          !selectedMarker &&
          !mouseOverInfoWindow.value
        ) {
          closeInfoWindow(infoWindow);
        }
      }, 200);
      destinationLocationName.value = '';
    };
    const closeInfoWindow = (infoWindow) => {
      infoWindow.close();
    };
    const addOpenInfoWindowListeners = (infoWindow, marker) => {
      let item = document
        .getElementById(`${marker.name}`)
        ?.closest('.gm-style-iw');
      if (item !== undefined && !item.hasAttribute('data-eventlistener')) {
        item.setAttribute('data-eventlistener', 'true');
        item.addEventListener('mouseleave', function () {
          polylineInstance.value?.setMap(null);

          if (
            selectedMarkers[selectedMarkers.length - 1].name !== marker.name
          ) {
            mouseOverInfoWindow.value = false;
            isHovering.value = false;
            closeInfoWindow(infoWindow);
          }
        });
        item.addEventListener('mouseenter', function () {
          polylineInstance.value?.setMap(null);
          isHovering.value = true;
          const oldestSelectedMarker = selectedMarkers[0];
          hoverPolylinePath.splice(0, hoverPolylinePath.length);
          hoverPolylinePath.push(
            marker.getPosition(),
            oldestSelectedMarker.getPosition(),
          );
        });
        item.addEventListener('click', function () {
          emit(
            'selectedDestinationName',
            marker.name.trim().toLocaleLowerCase(),
          );
        });
      }
    };
  },
};
</script>
<template>
  <div id="map" class="map"></div>
</template>
